import { inject, NgModule } from '@angular/core';
import {
  ApolloLink,
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  InMemoryCache,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import { createClient } from 'graphql-ws';
import { LogoutAction } from 'src/app/store/user/user.actions';
import { environment } from 'src/environments/environment';
import { CustomScalarLink } from './helpers/graphql';
import { CLIENT_INSTANCE_ID } from './services/realtime/client-instance';
import { SetRealtimeConnectionStateAction } from './store/misc/misc.actions';

@NgModule({
  exports: [],
  providers: [
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const store = inject(Store);
      const transloco = inject(TranslocoService);
      const clientInstanceId = inject(CLIENT_INSTANCE_ID);

      const http = httpLink.create({
        uri: environment.gqlEndpoint,
        withCredentials: true,
        extractFiles: body => extractFiles(body, isExtractableFile),
      });

      // Stamp every HTTP GraphQL request with the current active language
      // (`Accept-Language`, read at dispatch time so a language change takes
      // effect on the very next query) and this tab's client instance id,
      // which the realtime events the request causes carry back.
      const requestHeadersLink = new ApolloLink((operation, forward) => {
        const lang = transloco.getActiveLang();
        operation.setContext(
          ({ headers = {} }: { headers?: Record<string, string> }) => ({
            headers: {
              ...headers,
              ...(lang ? { 'Accept-Language': lang } : {}),
              'X-Client-Instance-Id': clientInstanceId,
            },
          })
        );
        return forward(operation);
      });

      // Create a WebSocket link. For subscriptions the language is sent via
      // graphql-ws's `connectionParams` (becomes part of the connection_init
      // payload) since custom headers can't be set on the WS upgrade.
      const ws = new GraphQLWsLink(
        createClient({
          url: environment.gqlEndpointWS,
          retryAttempts: Infinity,
          shouldRetry: () => true,
          keepAlive: 10000,
          connectionParams: () => ({
            acceptLanguage: transloco.getActiveLang() ?? '',
          }),
        })
      );

      const errorLink = new ErrorLink(({ error }) => {
        if (CombinedGraphQLErrors.is(error)) {
          error.errors.forEach(({ message, locations, path, extensions }) => {
            console.log(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
            if (extensions && extensions['code'] === 401) {
              console.log(`You have been logged out: ${message}`);
              store.dispatch(new LogoutAction('/login', {}));
            }
          });
        } else if (CombinedProtocolErrors.is(error)) {
          error.errors.forEach(({ message, extensions }) =>
            console.log(
              `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(
                extensions
              )}`
            )
          );
        } else {
          console.error(`[Network error]: ${error}`);
        }
      });

      ws.client.on('connecting', () => {
        store.dispatch(new SetRealtimeConnectionStateAction('connecting'));
      });

      ws.client.on('closed', () => {
        store.dispatch(new SetRealtimeConnectionStateAction('closed'));
      });

      ws.client.on('connected', () => {
        store.dispatch(new SetRealtimeConnectionStateAction('connected'));
      });

      ws.client.on('error', () => {
        store.dispatch(new SetRealtimeConnectionStateAction('error'));
      });

      // using the ability to split links, you can send data to each link
      // depending on what kind of operation is being sent
      const link = ApolloLink.split(
        // split based on operation type
        ({ query }) => {
          const { kind, operation } = getMainDefinition(query) as {
            kind: string;
            operation: string;
          };
          return kind === 'OperationDefinition' && operation === 'subscription';
        },
        ws,
        http
      );

      return {
        link: ApolloLink.from([
          errorLink,
          requestHeadersLink,
          CustomScalarLink,
          link,
        ]),
        cache: new InMemoryCache(),
        defaultOptions: {
          watchQuery: {
            errorPolicy: 'all',
          },
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          },
          mutate: {
            errorPolicy: 'all',
          },
        },
      };
    }),
  ],
})
export class GraphQLModule {}
